(() => {
  if (window.WMColorComparisonLoaded) return;
  window.WMColorComparisonLoaded = true;

  const S = {
    section: '[data-wm-color-comparison]',
    card: '[data-wm-card]',
    hotspot: '[data-wm-hotspot]',
    hotspotButton: '.wm-cc-hotspot__button',
    popover: '.wm-cc-hotspot__popover',
    search: '[data-wm-color-search]'
  };

  const normalize = (value = '') =>
    value.toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('de').trim();

  const closePopovers = (except = null) => {
    document.querySelectorAll(`${S.hotspot}.is-open`).forEach((hotspot) => {
      if (hotspot === except) return;
      hotspot.classList.remove('is-open');
      hotspot.querySelector(S.hotspotButton)?.setAttribute('aria-expanded', 'false');
      const popover = hotspot.querySelector(S.popover);
      if (popover) popover.hidden = true;
    });
  };

  const buildProductLists = (root = document) => {
    root.querySelectorAll(S.card).forEach((card) => {
      const list = card.querySelector('[data-wm-product-list]');
      if (!list) return;
      const seen = new Set();
      list.innerHTML = '';

      card.querySelectorAll(S.hotspot).forEach((hotspot) => {
        const title = hotspot.dataset.productTitle || '';
        const url = hotspot.dataset.productUrl || '';
        if (!title || !url) return;
        const key = `${title}::${url}`;
        if (seen.has(key)) return;
        seen.add(key);

        const link = document.createElement('a');
        link.href = url;
        link.className = 'wm-cc-product-link';
        if (hotspot.dataset.status === 'new') link.classList.add('wm-cc-product-link--new');
        link.textContent = title;
        list.appendChild(link);
      });
    });
  };

  const fillSuggestions = () => {
    const names = new Set();
    document.querySelectorAll(S.hotspot).forEach((hotspot) => {
      if (hotspot.closest('.wm-cc-lightbox')) return;
      const name = (hotspot.dataset.productTitle || '').trim();
      if (name) names.add(name);
    });
    document.querySelectorAll('[data-wm-suggestions]').forEach((list) => {
      list.innerHTML = '';
      [...names].sort((a, b) => a.localeCompare(b, 'de')).forEach((name) => {
        const option = document.createElement('option');
        option.value = name;
        list.appendChild(option);
      });
    });
  };

  const applySearch = (rawQuery, sourceInput = null) => {
    const query = normalize(rawQuery);
    let visibleCards = 0;

    document.querySelectorAll(S.section).forEach((section) => {
      let sectionMatches = 0;
      const cards = [...section.querySelectorAll(S.card)];

      cards.forEach((card) => {
        const cardText = normalize(`${card.dataset.cardTitle || ''} ${card.dataset.searchExtra || ''}`);
        const hotspots = [...card.querySelectorAll(S.hotspot)];
        let hotspotMatched = false;

        hotspots.forEach((hotspot) => {
          const match = Boolean(query && normalize(hotspot.dataset.search || '').includes(query));
          hotspot.classList.toggle('is-search-match', match);
          if (match) hotspotMatched = true;
        });

        const cardMatched = Boolean(query && cardText.includes(query));
        const matched = !query || cardMatched || hotspotMatched;
        card.hidden = !matched;
        card.classList.toggle('is-searching', Boolean(query && matched));
        card.classList.toggle('search-matched-by-card', cardMatched);

        if (matched) {
          sectionMatches++;
          visibleCards++;
        }
      });

      const noMatches = Boolean(query && sectionMatches === 0);
      section.classList.toggle('wm-cc-section--no-matches', noMatches);
      const ownsSearch = Boolean(section.querySelector('[data-wm-search-wrap]'));
      section.hidden = noMatches && !ownsSearch;

      const empty = section.querySelector('[data-wm-section-empty]');
      if (empty) empty.hidden = !noMatches;
    });

    document.querySelectorAll(S.search).forEach((input) => {
      if (input !== sourceInput) input.value = rawQuery;
      const clear = input.closest('[data-wm-search-wrap]')?.querySelector('[data-wm-search-clear]');
      if (clear) clear.hidden = !rawQuery;
    });

    document.querySelectorAll('[data-wm-search-status]').forEach((status) => {
      status.textContent = !query
        ? ''
        : visibleCards === 0
          ? 'Keine passenden Vergleichsfotos gefunden.'
          : visibleCards === 1
            ? '1 passendes Vergleichsfoto gefunden.'
            : `${visibleCards} passende Vergleichsfotos gefunden.`;
    });
  };

  let lightbox = null;
  let currentCard = null;
  let previousFocus = null;
  let scale = 1;
  let x = 0;
  let y = 0;
  const pointers = new Map();
  let pinchDistance = 0;
  let pinchScale = 1;
  let swipeStart = null;

  const visibleCards = () =>
    [...document.querySelectorAll(S.card)].filter((card) => {
      if (card.closest('.wm-cc-lightbox') || card.hidden) return false;
      const section = card.closest(S.section);
      return section && !section.hidden;
    });

  const renderTransform = () => {
    if (!lightbox) return;
    lightbox.querySelector('[data-wm-lb-transform]').style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    lightbox.querySelector('[data-wm-lb-zoom]').textContent = `${Math.round(scale * 100)} %`;
  };

  const resetTransform = () => { scale = 1; x = 0; y = 0; renderTransform(); };
  const setScale = (value) => {
    scale = Math.min(4, Math.max(1, value));
    if (scale === 1) { x = 0; y = 0; }
    renderTransform();
  };

  const createLightbox = () => {
    if (lightbox) return;
    lightbox = document.createElement('div');
    lightbox.className = 'wm-cc-lightbox';
    lightbox.hidden = true;
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-label', 'Vergrößerte Farbvergleichsansicht');
    lightbox.innerHTML = `
      <div class="wm-cc-lightbox__topbar">
        <p class="wm-cc-lightbox__title" data-wm-lb-title></p>
        <button type="button" class="wm-cc-lightbox__close" data-wm-lb-close aria-label="Großansicht schließen">×</button>
      </div>
      <div class="wm-cc-lightbox__stage" data-wm-lb-stage>
        <button type="button" class="wm-cc-lightbox__nav wm-cc-lightbox__prev" data-wm-lb-prev aria-label="Vorheriges Bild">‹</button>
        <div class="wm-cc-lightbox__transform" data-wm-lb-transform>
          <div class="wm-cc-lightbox__media">
            <img class="wm-cc-lightbox__image" data-wm-lb-image alt="">
            <div class="wm-cc-lightbox__hotspots" data-wm-lb-hotspots></div>
          </div>
        </div>
        <button type="button" class="wm-cc-lightbox__nav wm-cc-lightbox__next" data-wm-lb-next aria-label="Nächstes Bild">›</button>
      </div>
      <div class="wm-cc-lightbox__toolbar">
        <button type="button" class="wm-cc-lightbox__control" data-wm-lb-minus aria-label="Verkleinern">−</button>
        <span class="wm-cc-lightbox__zoom-value" data-wm-lb-zoom>100 %</span>
        <button type="button" class="wm-cc-lightbox__control" data-wm-lb-plus aria-label="Vergrößern">+</button>
        <button type="button" class="wm-cc-lightbox__control" data-wm-lb-reset aria-label="Zoom zurücksetzen">↺</button>
        <span class="wm-cc-lightbox__hint">Mausrad oder Zwei-Finger-Geste zum Zoomen</span>
      </div>`;
    document.body.appendChild(lightbox);

    lightbox.querySelector('[data-wm-lb-close]').addEventListener('click', closeLightbox);
    lightbox.querySelector('[data-wm-lb-prev]').addEventListener('click', () => navigate(-1));
    lightbox.querySelector('[data-wm-lb-next]').addEventListener('click', () => navigate(1));
    lightbox.querySelector('[data-wm-lb-plus]').addEventListener('click', () => setScale(scale + .35));
    lightbox.querySelector('[data-wm-lb-minus]').addEventListener('click', () => setScale(scale - .35));
    lightbox.querySelector('[data-wm-lb-reset]').addEventListener('click', resetTransform);

    const stage = lightbox.querySelector('[data-wm-lb-stage]');
    stage.addEventListener('wheel', (event) => {
      event.preventDefault();
      setScale(scale + (event.deltaY < 0 ? .25 : -.25));
    }, { passive: false });

    stage.addEventListener('pointerdown', (event) => {
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      stage.setPointerCapture?.(event.pointerId);
      if (pointers.size === 1) swipeStart = { x: event.clientX, y: event.clientY };
      if (pointers.size === 2) {
        const pts = [...pointers.values()];
        pinchDistance = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        pinchScale = scale;
      }
    });

    stage.addEventListener('pointermove', (event) => {
      if (!pointers.has(event.pointerId)) return;
      const previous = pointers.get(event.pointerId);
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (pointers.size === 2) {
        const pts = [...pointers.values()];
        const distance = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        if (pinchDistance) setScale(pinchScale * (distance / pinchDistance));
      } else if (pointers.size === 1 && scale > 1) {
        x += event.clientX - previous.x;
        y += event.clientY - previous.y;
        renderTransform();
      }
    });

    const pointerEnd = (event) => {
      const last = pointers.get(event.pointerId);
      pointers.delete(event.pointerId);
      if (pointers.size === 0 && scale === 1 && swipeStart && last) {
        const dx = last.x - swipeStart.x;
        const dy = last.y - swipeStart.y;
        if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.2) navigate(dx < 0 ? 1 : -1);
      }
      if (pointers.size < 2) pinchDistance = 0;
      if (pointers.size === 0) swipeStart = null;
    };
    stage.addEventListener('pointerup', pointerEnd);
    stage.addEventListener('pointercancel', pointerEnd);
  };

  const populateLightbox = (card) => {
    currentCard = card;
    closePopovers();
    resetTransform();

    const trigger = card.querySelector('[data-wm-open-lightbox]');
    const original = card.querySelector('.wm-cc-card__image');
    const image = lightbox.querySelector('[data-wm-lb-image]');
    image.src = trigger?.dataset.fullSrc || original?.currentSrc || original?.src || '';
    image.alt = trigger?.dataset.alt || original?.alt || '';
    lightbox.querySelector('[data-wm-lb-title]').textContent = card.dataset.cardTitle || 'Farbvergleich';

    const target = lightbox.querySelector('[data-wm-lb-hotspots]');
    target.innerHTML = '';
    const source = card.querySelector('[data-wm-hotspot-layer]');
    source?.querySelectorAll(S.hotspot).forEach((hotspot) => {
      const clone = hotspot.cloneNode(true);
      clone.classList.remove('is-open');
      clone.removeAttribute('data-shopify-editor-block');

      clone.querySelectorAll('[data-shopify-editor-block]').forEach((node) => {
        node.removeAttribute('data-shopify-editor-block');
      });

      const button = clone.querySelector(S.hotspotButton);
      if (button) button.setAttribute('aria-expanded', 'false');

      const popover = clone.querySelector(S.popover);
      if (popover) popover.hidden = true;

      target.appendChild(clone);
    });

    const cards = visibleCards();
    const showNav = cards.length > 1;
    lightbox.querySelector('[data-wm-lb-prev]').hidden = !showNav;
    lightbox.querySelector('[data-wm-lb-next]').hidden = !showNav;
  };

  const openLightbox = (card, trigger) => {
    createLightbox();
    previousFocus = trigger || document.activeElement;
    populateLightbox(card);
    lightbox.hidden = false;
    document.body.classList.add('wm-cc-lightbox-open');
    lightbox.querySelector('[data-wm-lb-close]').focus();
  };

  function closeLightbox() {
    if (!lightbox || lightbox.hidden) return;
    closePopovers();
    lightbox.hidden = true;
    document.body.classList.remove('wm-cc-lightbox-open');
    resetTransform();
    previousFocus?.focus?.();
  }

  const navigate = (direction) => {
    const cards = visibleCards();
    if (!cards.length || !currentCard) return;
    const index = cards.indexOf(currentCard);
    populateLightbox(cards[(index + direction + cards.length) % cards.length]);
  };

  document.addEventListener('click', (event) => {
    const hotspotButton = event.target.closest(S.hotspotButton);
    if (hotspotButton) {
      event.preventDefault();
      event.stopPropagation();
      const hotspot = hotspotButton.closest(S.hotspot);
      const opening = !hotspot.classList.contains('is-open');
      closePopovers(hotspot);
      hotspot.classList.toggle('is-open', opening);
      hotspotButton.setAttribute('aria-expanded', String(opening));
      const popover = hotspot.querySelector(S.popover);
      if (popover) popover.hidden = !opening;
      return;
    }

    const opener = event.target.closest('[data-wm-open-lightbox]');
    if (opener) {
      const card = opener.closest(S.card);
      if (card) openLightbox(card, opener);
      return;
    }

    const clear = event.target.closest('[data-wm-search-clear]');
    if (clear) {
      const input = clear.closest('[data-wm-search-wrap]')?.querySelector(S.search);
      if (input) {
        input.value = '';
        input.focus();
        applySearch('', input);
      }
      return;
    }

    if (!event.target.closest(S.hotspot)) closePopovers();
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches(S.search)) applySearch(event.target.value, event.target);
  });

  document.addEventListener('keydown', (event) => {
    if (!lightbox || lightbox.hidden) return;
    if (event.key === 'Escape') closeLightbox();
    else if (event.key === 'ArrowRight') navigate(1);
    else if (event.key === 'ArrowLeft') navigate(-1);
    else if (event.key === '+') setScale(scale + .35);
    else if (event.key === '-') setScale(scale - .35);
  });

  const initialize = (root = document) => {
    buildProductLists(root);
    fillSuggestions();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    event.target?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  });
})();
